<template>
  <div class="profile-page">
    <div class="page-container">
      <!-- Page Header -->
      <div class="page-header">
        <h1 class="page-title">{{ $t('profile.title') }}</h1>
        <p class="page-subtitle">{{ $t('profile.subtitle') }}</p>
      </div>

      <!-- User Information Card -->
      <div class="section-card">
        <div class="card-header">
          <h2 class="card-title">{{ $t('profile.userInfo') }}</h2>
        </div>
        <div class="card-content">
          <div class="info-grid">
            <div class="info-item">
              <label class="info-label">{{ $t('profile.name') }}</label>
              <p class="info-value">{{ user?.name || $t('profile.notSet') }}</p>
            </div>
            <div class="info-item">
              <label class="info-label">{{ $t('profile.email') }}</label>
              <p class="info-value">{{ user?.email || $t('profile.notSet') }}</p>
            </div>
            <div class="info-item">
              <label class="info-label">{{ $t('profile.memberSince') }}</label>
              <p class="info-value">{{ formatDate(user?.created_at) }}</p>
            </div>
            <div class="info-item">
              <label class="info-label">{{ $t('profile.language') }}</label>
              <div class="language-selector-wrapper">
                <LanguageSelector />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Theme Card -->
      <div class="section-card">
        <div class="card-header">
          <h2 class="card-title">{{ $t('profile.theme') }}</h2>
        </div>
        <div class="card-content">
          <p class="theme-hint">{{ $t('profile.themeDesc') }}</p>

          <div class="theme-options">
            <button
              v-for="option in THEMES"
              :key="option.key"
              type="button"
              class="theme-option"
              :class="{ selected: selectedTheme === option.key }"
              :disabled="savingTheme"
              @click="chooseTheme(option.key)"
            >
              <!-- The rows the setting gives, drawn at the same hairline as
                   every other mark in the app. A colour swatch would say
                   nothing: all three share the palette. -->
              <LineIcon
                :name="'density-' + option.key"
                :size="16"
                :stroke-width="1.8"
                class="theme-preview"
              />
              <span class="theme-name">{{ $t('profile.themes.' + option.key) }}</span>
              <span v-if="option.key === DEFAULT_THEME" class="theme-tag">
                {{ $t('profile.themeCurrent') }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Subscription Info Card -->
      <div class="section-card">
        <div class="card-header">
          <h2 class="card-title">{{ $t('profile.subscription') }}</h2>
          <router-link to="/subscription" class="btn-secondary">
            {{ $t('profile.manageSubscription') }}
          </router-link>
        </div>
        <div class="card-content">
          <div class="subscription-info">
            <div class="sub-item">
              <label class="sub-label">{{ $t('profile.plan') }}</label>
              <p class="sub-value">{{ planName }}</p>
            </div>
            <div class="sub-item">
              <label class="sub-label">{{ $t('profile.scansThisMonth') }}</label>
              <p class="sub-value">{{ scansUsed }} / {{ scanLimit }}</p>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: `${usagePercentage}%` }"></div>
              </div>
            </div>
            <div v-if="hardScanLimit > 0" class="sub-item">
              <label class="sub-label">{{ $t('profile.hardScans') }}</label>
              <p class="sub-value">{{ hardScansUsed }} / {{ hardScanLimit }}</p>
              <div class="progress-bar">
                <div class="progress-fill progress-fill-premium" :style="{ width: `${hardScanUsagePercentage}%` }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- API Keys Card -->
      <div class="section-card">
        <div class="card-header">
          <h2 class="card-title">{{ $t('profile.apiKeys') }}</h2>
          <button @click="showCreateKeyModal = true" class="btn-primary">
            <LineIcon name="plus" :size="15" :stroke-width="1.8" />
            {{ $t('profile.createNewKey') }}
          </button>
        </div>
        <div class="card-content">
          <div class="api-note">
            <LineIcon name="info" :size="14" :stroke-width="1.8" />
            <div>
              <p class="note-title">{{ $t('profile.apiKeyInfoTitle') }}</p>
              <p class="note-text">
                {{ $t('profile.apiKeyInfoText') }}
                <i18n-t keypath="profile.apiKeyInfoEndpoint" tag="span" scope="global">
                  <template #endpoint><code>/api/invoices-api</code></template>
                  <template #header><code>X-API-Key</code></template>
                </i18n-t>
              </p>
            </div>
          </div>

          <!-- API Keys List -->
          <div v-if="apiKeys.length > 0" class="api-keys-list">
            <div v-for="key in apiKeys" :key="key.id" class="api-key-item">
              <div class="key-info">
                <div class="key-name">{{ key.name }}</div>
                <div class="key-value">{{ key.masked_key }}</div>
                <div class="key-meta">{{ $t('profile.keyCreated', { date: formatDate(key.created_at) }) }}</div>
              </div>
              <div class="key-actions">
                <button @click="deleteApiKey(key.id)" class="btn-delete">
                  <LineIcon name="trash" :size="13" :stroke-width="1.8" />
                  {{ $t('profile.deleteKey') }}
                </button>
              </div>
            </div>
          </div>
          <div v-else class="no-keys">
            <p>{{ $t('profile.noKeys') }}</p>
          </div>

          <div class="api-example">
            <p class="example-title">{{ $t('profile.exampleUsage') }}</p>
            <pre class="example-code">curl -X POST {{ appDomain }}/api/invoices-api -H "X-API-Key: your_api_key_here" -F "file=@invoice.pdf"</pre>
          </div>
        </div>
      </div>

      <!-- Create API Key Modal -->
      <div v-if="showCreateKeyModal" class="modal-overlay" @click="showCreateKeyModal = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Create New API Key</h3>
            <button @click="showCreateKeyModal = false" class="btn-close">×</button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>Key Name</label>
              <input
                v-model="newKeyName"
                type="text"
                placeholder="e.g., Production Server"
                class="form-input"
              />
            </div>
            <div v-if="newCreatedKey" class="key-created-alert">
              <p class="alert-title">⚠️ Save this key now!</p>
              <p class="alert-text">You won't be able to see it again.</p>
              <div class="key-display">
                <code>{{ newCreatedKey }}</code>
                <button @click="copyKey" class="btn-copy">
                  {{ keyCopied ? 'Copied!' : 'Copy' }}
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="showCreateKeyModal = false" class="btn-secondary">Close</button>
            <button v-if="!newCreatedKey" @click="createApiKey" class="btn-primary">Create Key</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, computed, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useScanStore } from '@/stores/scan';
import { useUiStore } from '@/stores/ui';
import api from '@/utils/api';
import LanguageSelector from '@/components/LanguageSelector.vue';
import { THEMES, DEFAULT_THEME, applyTheme, storeTheme, readStoredTheme } from '@/utils/theme';

const authStore = useAuthStore();
const scanStore = useScanStore();
const uiStore = useUiStore();
const appDomain = window.location.origin;

const user = computed(() => authStore.user);
const subscription = computed(() => authStore.subscription);

const selectedTheme = ref(authStore.theme || readStoredTheme());
const savingTheme = ref(false);

/**
 * Paint the theme straight away, then save it.
 *
 * Painting first is deliberate: the point of the control is to see the
 * change, and a round trip to the server before anything happens makes it
 * feel broken. If the save fails the previous choice is painted back, so the
 * screen never claims something the account does not hold.
 */
const chooseTheme = async (theme) => {
  if (savingTheme.value || theme === selectedTheme.value) return;

  const previous = selectedTheme.value;

  selectedTheme.value = theme;
  applyTheme(theme);
  storeTheme(theme);
  savingTheme.value = true;

  try {
    await api.post('/api/user/theme', { theme });
    authStore.settings = { ...(authStore.settings || {}), theme };
  } catch (error) {
    selectedTheme.value = previous;
    applyTheme(previous);
    storeTheme(previous);
    uiStore.showToast({
      message: error.response?.data?.message || error.message,
      type: 'error',
    });
  } finally {
    savingTheme.value = false;
  }
};

const apiKeys = ref([]);
const showCreateKeyModal = ref(false);
const newKeyName = ref('');
const newCreatedKey = ref('');
const keyCopied = ref(false);

const scansUsed = computed(() => subscription.value?.scans_used || 0);
const scanLimit = computed(() => subscription.value?.scan_limit || 100);
const hardScansUsed = computed(() => subscription.value?.hard_scans_used || 0);
const hardScanLimit = computed(() => subscription.value?.hard_scan_limit || 0);

const usagePercentage = computed(() => {
  if (!scanLimit.value) return 0;
  return Math.min((scansUsed.value / scanLimit.value) * 100, 100);
});

const hardScanUsagePercentage = computed(() => {
  if (!hardScanLimit.value) return 0;
  return Math.min((hardScansUsed.value / hardScanLimit.value) * 100, 100);
});

const planName = computed(() => {
  const limit = scanLimit.value;
  if (limit <= 100) return 'Free Plan';
  if (limit <= 1000) return 'Pro Plan';
  return 'Enterprise Plan';
});

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const loadApiKeys = async () => {
  try {
    const response = await api.get('/api/profile/api-keys');
    apiKeys.value = response.data.api_keys;
  } catch (error) {
    console.error('Failed to load API keys:', error);
  }
};

const createApiKey = async () => {
  try {
    const response = await api.post('/api/profile/api-keys', {
      name: newKeyName.value || 'API Key'
    });

    newCreatedKey.value = response.data.api_key;
    newKeyName.value = '';

    uiStore.showToast({
      message: 'API key created successfully!',
      type: 'success'
    });

    await loadApiKeys();
  } catch (error) {
    uiStore.showToast({
      message: error.response?.data?.message || 'Failed to create API key',
      type: 'error'
    });
  }
};

const deleteApiKey = async (keyId) => {
  if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
    return;
  }

  try {
    await api.delete(`/api/profile/api-keys/${keyId}`);

    uiStore.showToast({
      message: 'API key deleted successfully',
      type: 'success'
    });

    await loadApiKeys();
  } catch (error) {
    uiStore.showToast({
      message: error.response?.data?.message || 'Failed to delete API key',
      type: 'error'
    });
  }
};

const copyKey = () => {
  navigator.clipboard.writeText(newCreatedKey.value);
  keyCopied.value = true;
  setTimeout(() => {
    keyCopied.value = false;
  }, 2000);
};

onMounted(async () => {
  await Promise.all([
    authStore.fetchUser(),
    loadApiKeys()
  ]);
});
</script>

<style scoped>
/* aligned with the public site design (light, violet accent, Plus Jakarta Sans) */
.profile-page {
  --violet: #2b82f0; --violet-hover: #1a6fdc; --ink: #1f2933; --muted: #6b7280;
  --line: rgba(31,41,51,0.08); --card: #ffffff; --pill: 999px;
  background: #f4f3f8; min-height: 100vh; padding: 44px 0 80px;
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.01em; color: var(--ink);
}
.profile-page button, .profile-page input, .profile-page select { font-family: inherit; }
.page-container { max-width: 900px; margin: 0 auto; padding: 0 24px; }
.page-header { margin-bottom: 32px; }
.page-title { font-size: clamp(28px, 3.6vw, 42px); font-weight: 700; letter-spacing: -0.035em; color: var(--ink); margin: 0 0 8px; }
.page-subtitle { font-size: 16px; color: var(--muted); margin: 0; }

.section-card { background: var(--card); border: 1px solid var(--line); border-radius: 22px; box-shadow: 0 24px 56px -42px rgba(31,41,51,0.3); margin-bottom: 22px; overflow: hidden; }
.card-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 22px 28px; border-bottom: 1px solid var(--line); }
.card-title { font-size: 18px; font-weight: 700; color: var(--ink); margin: 0; letter-spacing: -0.01em; }
.card-content { padding: 26px 28px; }

.info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 22px; }
.info-item { display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
.info-label { font-size: 12px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.05em; }
.info-value { font-size: 15.5px; font-weight: 600; color: var(--ink); margin: 0; }

.subscription-info { display: flex; flex-direction: column; gap: 20px; }
.sub-item { display: flex; flex-direction: column; gap: 6px; }
.sub-label { font-size: 13px; font-weight: 600; color: var(--muted); }
.sub-value { font-size: 16px; font-weight: 700; color: var(--ink); margin: 0; }
.progress-bar { height: 10px; background: #eef1f6; border-radius: var(--pill); overflow: hidden; margin-top: 4px; }
.progress-fill { height: 100%; background: linear-gradient(90deg, #5aa2ff, #2b82f0); border-radius: var(--pill); transition: width 0.5s ease; }
.progress-fill-premium { background: linear-gradient(90deg, #a78bfa, #7c5cff); }

.btn-primary { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: var(--pill); border: none; background: var(--violet); color: #fff; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 14px 26px -14px rgba(43,130,240,0.6); text-decoration: none; }
.btn-primary:hover { background: var(--violet-hover); transform: translateY(-2px); }
.btn-secondary { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: var(--pill); border: 1px solid var(--line); background: #fff; color: var(--ink); font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; text-decoration: none; }
.btn-secondary:hover { border-color: var(--violet); color: var(--violet); transform: translateY(-2px); }

.api-note { display: flex; gap: 14px; align-items: flex-start; background: rgba(43,130,240,0.06); border: 1px solid rgba(43,130,240,0.18); border-radius: 14px; padding: 16px 18px; margin-bottom: 22px; color: var(--violet); }
.note-title { font-size: 14px; font-weight: 700; color: var(--ink); margin: 0 0 4px; }
.note-text { font-size: 13.5px; color: var(--muted); line-height: 1.55; margin: 0; }
.note-text code { background: rgba(43,130,240,0.1); color: var(--violet); padding: 1px 6px; border-radius: 5px; font-size: 12.5px; }

.api-keys-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 22px; }
.api-key-item { display: flex; align-items: center; justify-content: space-between; gap: 16px; background: #fbfbfd; border: 1px solid var(--line); border-radius: 14px; padding: 16px 18px; }
.key-name { font-size: 14.5px; font-weight: 700; color: var(--ink); }
.key-value { font-size: 13px; color: var(--muted); font-family: 'Fira Code', monospace; margin-top: 2px; }
.key-meta { font-size: 12px; color: #9aa3af; margin-top: 4px; }
.btn-delete { display: inline-flex; align-items: center; gap: 7px; padding: 9px 16px; border-radius: var(--pill); border: 1px solid rgba(239,68,68,0.25); background: #fff; color: #ef4444; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; }
.btn-delete:hover { background: #ef4444; color: #fff; border-color: #ef4444; }
.no-keys { color: var(--muted); font-size: 14px; padding: 8px 0 22px; }

.api-example { background: #161d27; border-radius: 14px; padding: 18px 20px; }
.example-title { font-size: 12.5px; font-weight: 600; color: rgba(255,255,255,0.6); margin: 0 0 10px; }
.example-code { font-family: 'Fira Code', monospace; font-size: 12.5px; color: rgba(255,255,255,0.85); white-space: pre-wrap; word-break: break-all; margin: 0; line-height: 1.6; }

.modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; -webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px); }
.modal-content { background: #fff; border-radius: 22px; width: 100%; max-width: 460px; box-shadow: 0 40px 90px -40px rgba(31,41,51,0.5); overflow: hidden; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 22px 26px; border-bottom: 1px solid var(--line); }
.modal-header h3 { font-size: 18px; font-weight: 700; color: var(--ink); margin: 0; }
.btn-close { background: none; border: none; font-size: 26px; line-height: 1; color: var(--muted); cursor: pointer; }
.btn-close:hover { color: var(--ink); }
.modal-body { padding: 24px 26px; }
.form-group { display: flex; flex-direction: column; gap: 8px; }
.form-group label { font-size: 13.5px; font-weight: 600; color: var(--ink); }
.form-input { width: 100%; padding: 12px 16px; border: 1px solid var(--line); border-radius: 12px; font-size: 15px; color: var(--ink); background: #fbfbfd; transition: all 0.2s ease; }
.form-input:focus { outline: none; border-color: var(--violet); box-shadow: 0 0 0 3px rgba(43,130,240,0.12); background: #fff; }
.key-created-alert { margin-top: 18px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 12px; padding: 16px; }
.alert-title { font-size: 14px; font-weight: 700; color: #b45309; margin: 0 0 4px; }
.alert-text { font-size: 13px; color: #92700e; margin: 0 0 12px; }
.key-display { display: flex; align-items: center; gap: 10px; background: #fff; border: 1px solid var(--line); border-radius: 10px; padding: 10px 12px; }
.key-display code { flex: 1; font-family: 'Fira Code', monospace; font-size: 12.5px; color: var(--ink); word-break: break-all; }
.btn-copy { padding: 7px 14px; border-radius: var(--pill); border: none; background: var(--violet); color: #fff; font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap; }
.btn-copy:hover { background: var(--violet-hover); }
.modal-footer { display: flex; align-items: center; justify-content: flex-end; gap: 12px; padding: 18px 26px; border-top: 1px solid var(--line); }

/* Theme picker */
.theme-hint { margin: 0 0 16px; font-size: 14px; color: var(--muted); }

.theme-options { display: flex; flex-wrap: wrap; gap: 12px; }

.theme-option {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px;
  background: var(--card); border: 1px solid var(--line); border-radius: 14px;
  cursor: pointer; transition: all 0.2s ease; font-family: inherit;
}
.theme-option:hover:not(:disabled) {
  border-color: #cbd5e1; transform: translateY(-2px);
  box-shadow: 0 14px 30px -18px rgba(31,41,51,0.35);
}
/* The chosen one is ringed rather than filled: a fill would compete with the
   swatches, which are the thing being compared. */
.theme-option.selected {
  border-color: var(--violet);
  box-shadow: 0 0 0 3px rgba(43,130,240,0.12);
}
.theme-option:disabled { opacity: 0.6; cursor: default; }

/* Three bars at the spacing the setting gives. It shows what is actually
   being chosen, which a colour swatch could not: all three share one palette. */
.theme-preview {
  display: flex; flex-direction: column; justify-content: center;
  width: 26px; height: 26px; flex-shrink: 0;
}
.theme-preview span { display: block; height: 3px; border-radius: 2px; background: var(--violet); opacity: 0.75; }
.theme-preview--spacious span + span { margin-top: 7px; }
.theme-preview--compact span + span { margin-top: 4px; }
.theme-preview--tabular span + span { margin-top: 2px; }

.theme-name { font-size: 14px; font-weight: 600; color: var(--ink); }

.theme-tag {
  font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--muted); background: rgba(31,41,51,0.05);
  padding: 3px 8px; border-radius: 999px;
}

@media (max-width: 600px) {
  .info-grid { grid-template-columns: 1fr; }
  .card-header { flex-direction: column; align-items: flex-start; }
  .api-key-item { flex-direction: column; align-items: flex-start; }
  .theme-options { flex-direction: column; }
  .theme-option { width: 100%; }
}

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #64748b;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #64748b;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* The row of answers under a question — "delete these seven?" — is a footer
   like any other: a hairline above it and the two buttons at its end. */
.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid #dde3ea;
    background: #fff;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── The rest of the buttons ───────────────────────────────────────────
 *
 * The chrome above settles the ones named primary, secondary, cancel and
 * save; these carry names of their own and so kept the old 15px-in-a-49px-box
 * shape. Same geometry as the others: a 32px box, a 13px label, 8px corner,
 * and a filled face only where something is being committed. */
.btn-copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  box-shadow: none;
  transition: all 0.15s ease;
  width: auto;
}

.btn-copy {
  background: #fff;
  border: 1px solid #dde3ea;
  color: #1e293b;
}

.btn-copy:hover:not(:disabled) {
  background: #fff;
  border-color: #0697e4;
  color: #0697e4;
  box-shadow: none;
  transform: none;
}


/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}

/* ── The profile, at the product's scale ───────────────────────────────
 *
 * Another page built like a public one: a 42px heading over a column 852px
 * wide, cards with 24px padding and soft shadows, 14px body text. Inside the
 * application it is a settings page, and settings are read in rows. */
/* The same measure as the dashboard — 1400px with a 16px gutter — so a card
   here starts on the same line as a card there. */
.page-container {
  max-width: 1400px;
  padding: 0 16px;
}

.page-header {
  margin-bottom: 16px;
  text-align: left;
}

.page-title {
  font-size: 20px;
  font-weight: 800;
  margin: 0 0 2px;
}

.page-subtitle {
  font-size: 12px;
}

.section-card {
  max-width: none;
  margin: 0 0 12px;
  border: 1px solid #dde3ea;
  border-radius: 12px;
  box-shadow: none;
}

.card-header {
  padding: 9px 14px;
  border-bottom: 1px solid #dde3ea;
}

.card-title {
  font-size: 13px;
  font-weight: 700;
}

.card-content {
  padding: 12px 14px;
}

/* Who is signed in: label over value, four to a row. */
.info-grid {
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px 20px;
}

.info-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #94a3b8;
  margin-bottom: 1px;
}

.info-value {
  font-size: 13px;
  font-weight: 600;
}

/* How tight the interface is set. */
.theme-hint {
  font-size: 11px;
  margin-bottom: 8px;
}

.theme-options {
  gap: 8px;
}

.theme-option {
  padding: 7px 12px;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  font-size: 12px;
  box-shadow: none;
}

.theme-option:hover {
  border-color: var(--primary, #0697e4);
  transform: none;
  box-shadow: none;
}

.theme-option.selected {
  border-color: var(--primary, #0697e4);
  background: #f4f9fd;
  box-shadow: none;
}

.theme-name {
  font-size: 12px;
  font-weight: 600;
}

.theme-tag {
  padding: 1px 5px;
  font-size: 9px;
  border-radius: 4px;
}

/* The subscription summary. */
.sub-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #94a3b8;
}

.sub-value {
  font-size: 13px;
  font-weight: 700;
}

.sub-item {
  margin-bottom: 10px;
}

.progress-bar {
  height: 4px;
  background: #f1f5f9;
  border-radius: 2px;
}

/* The API section: a quiet note, a plain list, one example. */
.api-note {
  gap: 8px;
  padding: 8px 10px;
  margin-bottom: 12px;
  background: #f4f9fd;
  border: 1px solid #dde3ea;
  border-left: 2px solid var(--primary, #0697e4);
  border-radius: 8px;
}

.note-title {
  font-size: 12px;
  font-weight: 700;
  margin: 0 0 1px;
}

.note-text {
  font-size: 11px;
  line-height: 1.45;
}

.note-text code {
  padding: 0 4px;
  font-size: 10px;
  border-radius: 4px;
}

.api-keys-list {
  display: flex;
  flex-direction: column;
}

.api-key-item {
  padding: 7px 0;
  border: none;
  border-top: 1px solid #f1f5f9;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.key-name {
  font-size: 12px;
  font-weight: 700;
}

.key-value {
  font-size: 11px;
}

.key-meta {
  font-size: 10px;
  color: #94a3b8;
}

.btn-delete {
  height: 26px;
  padding: 0 10px;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  color: #e04545;
  box-shadow: none;
}

.btn-delete:hover {
  border-color: #e04545;
  background: #fff;
  transform: none;
}

.no-keys {
  font-size: 12px;
  padding: 4px 0 12px;
}

.api-example {
  padding: 10px 12px;
  border-radius: 8px;
}

.example-title {
  font-size: 10px;
  margin: 0 0 6px;
}

.example-code {
  font-size: 11px;
  line-height: 1.5;
}

/* The mark in the density picker: the app's blue, and it grows a little
   under the pointer like the others. */
.theme-preview {
  color: var(--primary, #0697e4);
  flex-shrink: 0;
  transition: transform 0.15s ease;
}

.theme-option:hover:not(:disabled) .theme-preview {
  transform: scale(1.12);
}

/* The greys, one step darker.
 *
 * Small text in #94a3b8 on white sits around 2.9:1 — fine for a hint that is
 * glanced at, thin for a label someone is reading. The labels move to the
 * slate the rest of the app uses for secondary text, and the running text to
 * a darker grey still. */
.page-subtitle,
.theme-hint,
.note-text,
.no-keys,
.key-value {
  color: #5b6472;
}

.info-label,
.sub-label,
.key-meta,
.example-title {
  color: #64748b;
}

/* Darker again, to the same values the plans page uses. */
.page-subtitle,
.theme-hint,
.note-text,
.no-keys,
.key-value {
  color: #4b5563;
}

.info-label,
.sub-label,
.key-meta,
.example-title {
  color: #5b6472;
}
</style>
